import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, Sparkles, Loader2, CheckCircle2, RotateCw, Trash2 } from "lucide-react";

/**
 * OCR Expense Dialog — upload a receipt image, LLM vision extracts
 * fields, user reviews and saves as an Expense.
 *
 * Prop `onSaved(expense)` — called after successful create.
 */
export default function OcrExpenseDialog({
  open,
  onOpenChange,
  onSaved,
  suppliers = [],
  categories = [],
  accounts = [],
  localized,
}) {
  const { t, lang } = useI18n();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [form, setForm] = useState(null);
  // Sprint 29.4 — OCR classification state.
  const [saveAs, setSaveAs]   = useState("expense");   // expense | invoice
  const [docType, setDocType] = useState("expense_receipt");

  const reset = () => {
    setDrag(false); setBusy(false); setSaving(false); setPreviewUrl("");
    setExtracted(null); setForm(null); setConfidence(0);
    setSaveAs("expense"); setDocType("expense_receipt");
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const upload = async (file) => {
    if (!file) return;
    const okMime = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!okMime.includes(file.type)) {
      toast.error(lang === "tr" ? "Sadece JPEG / PNG / WebP" : "Nur JPEG / PNG / WebP");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(lang === "tr" ? "Görsel 8 MB'dan büyük" : "Bild > 8 MB");
      return;
    }
    setBusy(true);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/ocr-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setExtracted(data);
      setConfidence(data.confidence || 0);
      // Sprint 29.4 — classify document + default the Save-as toggle.
      setSaveAs(data.suggested_kind === "invoice" ? "invoice" : "expense");
      setDocType(data.document_type || "expense_receipt");
      setForm({
        date: data.date,
        amount: data.amount || 0,
        tax_amount: data.tax_amount || 0,
        vendor: data.vendor || "",
        description: data.description || "",
        supplier_id: data.supplier_id || "",
        category_id: "",
        bank_account_id: "",
        payment_status: "paid",
        invoice_number: data.invoice_number || "",
        due_date: data.due_date || "",
        line_items: Array.isArray(data.line_items) ? data.line_items : [],
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setPreviewUrl("");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) upload(f);
  };

  const save = async () => {
    if (!form) return;
    if (!(form.amount > 0)) return toast.error(lang === "tr" ? "Tutar > 0" : "Betrag > 0");
    if (!form.vendor?.trim()) return toast.error(lang === "tr" ? "Tedarikçi zorunlu" : "Lieferant erforderlich");
    setSaving(true);
    try {
      if (saveAs === "invoice") {
        const gross = +form.amount;
        const vat   = +form.tax_amount;
        const net   = Math.max(0, gross - vat);
        const invPayload = {
          type: "ap",
          number: form.invoice_number || `OCR-${Date.now()}`,
          party_name: form.vendor,
          supplier_id: form.supplier_id || null,
          issue_date: form.date,
          due_date: form.due_date || form.date,
          currency: "EUR",
          amount_net: net,
          amount_tax: vat,
          amount_total: gross,
          status: form.payment_status === "paid" ? "paid" : (form.payment_status === "partial" ? "partial" : "open"),
          notes: form.description || "",
          lines: (form.line_items || []).map((li) => ({
            description: li.name || li.description || "",
            qty: +li.qty || 1,
            unit_price: +(li.unit_price ?? (li.total || 0) / Math.max(1, +li.qty || 1)),
            tax_rate: 0,
            total: +(li.total ?? (+li.unit_price || 0) * (+li.qty || 1)),
          })),
        };
        const { data } = await api.post("/invoices", invPayload);
        toast.success(lang === "tr" ? "Fatura kaydedildi" : "Rechnung gespeichert");
        close(false);
        onSaved?.(data);
      } else {
        const payload = {
          ...form,
          amount: +form.amount,
          tax_amount: +form.tax_amount,
          supplier_id: form.supplier_id || null,
          category_id: form.category_id || null,
          bank_account_id: form.bank_account_id || null,
          source: "ocr",
        };
        const { data } = await api.post("/expenses", payload);
        toast.success(lang === "tr" ? "Gider kaydedildi" : "Ausgabe gespeichert");
        close(false);
        onSaved?.(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const updateLineItem = (idx, patch) => {
    setForm((f) => ({ ...f, line_items: f.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  };
  const removeLineItem = (idx) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const confBadgeTone = confidence >= 0.7 ? "emerald" : confidence >= 0.4 ? "amber" : "red";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="!max-w-4xl !w-full
                   !left-0 !top-auto !bottom-0 !right-auto !translate-x-0 !translate-y-0
                   !h-[100dvh] !max-h-[100dvh]
                   sm:!left-[50%] sm:!top-[50%] sm:!bottom-auto sm:!right-auto
                   sm:!translate-x-[-50%] sm:!translate-y-[-50%]
                   sm:!h-auto sm:!max-h-[92vh] sm:!w-[calc(100vw-2rem)]
                   !p-0 !gap-0 flex flex-col rounded-none sm:rounded-xl overflow-hidden"
        data-testid="ocr-dialog"
      >
        <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b bg-background">
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {lang === "tr" ? "OCR ile Gider Oluştur" : "Ausgabe per OCR"}
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid="ocr-body"
        >
          {!extracted && !busy && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-border bg-muted/20"}`}
            data-testid="ocr-drop"
          >
            <div className="w-14 h-14 rounded-2xl bg-background border shadow-elev-1 flex items-center justify-center mx-auto mb-4">
              <Camera className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="font-display font-semibold">
              {lang === "tr" ? "Fatura / fiş fotoğrafı yükle" : "Beleg-Foto hochladen"}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
              {lang === "tr"
                ? "AI görüntüyü okur, tedarikçi/tutar/vergi bilgilerini otomatik doldurur."
                : "KI liest den Beleg und füllt Lieferant/Betrag/MwSt. automatisch."}
            </p>
            <label className="mt-5 inline-flex">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={(e) => upload(e.target.files?.[0])}
                className="hidden"
                data-testid="ocr-file-input"
              />
              <Button asChild className="h-10 rounded-lg cursor-pointer shadow-elev-2">
                <span><Upload className="w-4 h-4 mr-2" />{lang === "tr" ? "Görsel seç" : "Bild wählen"}</span>
              </Button>
            </label>
            <p className="text-[11px] text-muted-foreground mt-3 font-mono">JPEG · PNG · WebP · ≤ 8 MB</p>
          </div>
        )}

        {busy && (
          <div className="flex flex-col items-center justify-center py-14">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <div className="mt-3 font-display font-semibold">{lang === "tr" ? "AI görüntüyü okuyor..." : "KI liest den Beleg..."}</div>
            <p className="text-xs text-muted-foreground mt-1">{lang === "tr" ? "Yaklaşık 5-15 saniye sürebilir" : "5–15 Sekunden"}</p>
          </div>
        )}

        {extracted && form && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Left: preview image + confidence */}
            <div className="md:col-span-2 space-y-3">
              {previewUrl && (
                <div className="rounded-xl border overflow-hidden bg-muted/30 aspect-[3/4] flex items-center justify-center">
                  <img src={previewUrl} alt="" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant="outline"
                  className={`rounded-md ${
                    confBadgeTone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    confBadgeTone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-red-50 text-red-700 border-red-200"
                  }`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {lang === "tr" ? "Güven" : "Vertrauen"} {(confidence * 100).toFixed(0)}%
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {lang === "tr" ? "Alanları kontrol et ve düzenle" : "Felder prüfen und bearbeiten"}
                </span>
              </div>
            </div>

            {/* Right: extracted form */}
            <div className="md:col-span-3 space-y-3">
              {/* Sprint 29.4 — Save-as classification (Gider vs Fatura) */}
              <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-transparent p-3 space-y-2"
                   data-testid="ocr-save-as">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">
                    {lang === "tr"
                      ? "Bu belge nasıl kaydedilsin?"
                      : "Wie soll dieses Dokument gespeichert werden?"}
                  </div>
                  <Badge variant="secondary" className="rounded-md text-[10px]" data-testid="ocr-doc-type-badge">
                    {({
                      expense_receipt:  lang === "tr" ? "Küçük fiş" : "Kleiner Beleg",
                      supplier_invoice: lang === "tr" ? "Tedarikçi faturası" : "Lieferantenrechnung",
                      utility_invoice:  lang === "tr" ? "Fatura (kamu)" : "Versorger",
                      rent_invoice:     lang === "tr" ? "Kira" : "Miete",
                      other:            lang === "tr" ? "Diğer" : "Sonstige",
                    })[docType] || docType}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSaveAs("expense")}
                    data-testid="ocr-save-as-expense"
                    data-active={saveAs === "expense"}
                    className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-all
                                ${saveAs === "expense"
                                  ? "border-primary bg-primary text-primary-foreground shadow-elev-2"
                                  : "hover:border-primary/60 bg-background"}`}
                  >
                    {lang === "tr" ? "Gider" : "Ausgabe"}
                    <div className="text-[10px] font-normal opacity-80">
                      {lang === "tr" ? "Küçük fiş / kasa" : "Kleiner Beleg / Kasse"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSaveAs("invoice")}
                    data-testid="ocr-save-as-invoice"
                    data-active={saveAs === "invoice"}
                    className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-all
                                ${saveAs === "invoice"
                                  ? "border-primary bg-primary text-primary-foreground shadow-elev-2"
                                  : "hover:border-primary/60 bg-background"}`}
                  >
                    {lang === "tr" ? "Fatura / Ödenecek" : "Rechnung / AP"}
                    <div className="text-[10px] font-normal opacity-80">
                      {lang === "tr" ? "Tedarikçi, kira, kamu faturası" : "Lieferant, Miete, Versorger"}
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t("date")}>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 rounded-lg" data-testid="ocr-date" />
                </Field>
                <Field label={`${t("amount")} (€)`}>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 rounded-lg font-mono" data-testid="ocr-amount" />
                </Field>
              </div>
              {saveAs === "invoice" && (
                <div className="grid grid-cols-2 gap-3" data-testid="ocr-invoice-fields">
                  <Field label={lang === "tr" ? "Fatura No" : "Rechnungsnr."}>
                    <Input value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="h-9 rounded-lg font-mono" data-testid="ocr-invoice-number" />
                  </Field>
                  <Field label={lang === "tr" ? "Vade" : "Fällig"}>
                    <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-9 rounded-lg font-mono" data-testid="ocr-due-date" />
                  </Field>
                </div>
              )}
              <Field label={t("vendor")}>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="h-9 rounded-lg" data-testid="ocr-vendor" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={lang === "tr" ? "Tedarikçi" : "Lieferant"}>
                  <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-9 rounded-lg" data-testid="ocr-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("category")}>
                  <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{localized ? localized(c.name) : (c.name?.tr || c.name?.de || "")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`${t("taxAmount")} (€)`}>
                  <Input type="number" step="0.01" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} className="h-9 rounded-lg font-mono" />
                </Field>
                <Field label={t("paymentStatus")}>
                  <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                    <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">{t("paid")}</SelectItem>
                      <SelectItem value="partial">{t("partial")}</SelectItem>
                      <SelectItem value="unpaid">{t("unpaid")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label={t("description")}>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg" />
              </Field>

              {form.line_items.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                    {lang === "tr" ? "Kalemler" : "Positionen"} ({form.line_items.length})
                  </div>
                  <div className="border rounded-lg overflow-hidden shadow-elev-1">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 sticky top-0 z-10">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold">{lang === "tr" ? "Ürün" : "Artikel"}</th>
                          <th className="text-right px-2 py-1.5 font-semibold w-14">Qty</th>
                          <th className="text-right px-2 py-1.5 font-semibold w-20">Unit</th>
                          <th className="text-right px-2 py-1.5 font-semibold w-20">Total</th>
                          <th className="w-6"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {form.line_items.map((li, idx) => (
                          <tr key={idx} data-testid={`ocr-line-${idx}`}>
                            <td className="px-2 py-1"><Input value={li.name} onChange={(e) => updateLineItem(idx, { name: e.target.value })} className="h-7 text-xs rounded-md" /></td>
                            <td className="px-2 py-1"><Input type="number" step="0.01" value={li.qty} onChange={(e) => updateLineItem(idx, { qty: +e.target.value || 0 })} className="h-7 text-xs rounded-md font-mono text-right" /></td>
                            <td className="px-2 py-1"><Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(idx, { unit_price: +e.target.value || 0 })} className="h-7 text-xs rounded-md font-mono text-right" /></td>
                            <td className="px-2 py-1"><Input type="number" step="0.01" value={li.total} onChange={(e) => updateLineItem(idx, { total: +e.target.value || 0 })} className="h-7 text-xs rounded-md font-mono text-right" /></td>
                            <td className="px-2 py-1"><button onClick={() => removeLineItem(idx)} className="text-destructive hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    {lang === "tr"
                      ? "Kalemler kaydedilir — ileride envanterle otomatik eşleşecek."
                      : "Positionen werden gespeichert — künftig automatischer Inventar-Sync."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        <div
          className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 py-3 sm:px-6
                     border-t bg-background/95 backdrop-blur
                     [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]"
          data-testid="ocr-footer"
        >
          {extracted && (
            <Button variant="outline" onClick={reset} className="rounded-lg min-h-[44px]" data-testid="ocr-retry">
              <RotateCw className="w-4 h-4 mr-2" /> {lang === "tr" ? "Farklı görsel" : "Anderes Bild"}
            </Button>
          )}
          <div className="flex-1 hidden sm:block" />
          <Button variant="ghost" onClick={() => close(false)} className="rounded-lg min-h-[44px]">{t("cancel")}</Button>
          {extracted && form && (
            <Button onClick={save} disabled={saving} className="rounded-lg shadow-elev-2 min-h-[44px]" data-testid="ocr-save">
              {saving ? t("loading")
                : (saveAs === "invoice"
                    ? (lang === "tr" ? "Fatura olarak kaydet" : "Als Rechnung speichern")
                    : (lang === "tr" ? "Gider olarak kaydet" : "Als Ausgabe speichern"))}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
